import React, { useState } from "react";
import { useFormContext, FormCheckbox, FormTextInput } from "@jsq/ds/forms";
import { CountrySelectionDropDown } from "../../../../../common/components/CountrySelectionDropDown";
import { FieldGroup } from "../../../../../common/components/Form/Layout/FieldGroup";
import { SimpleFormRow } from "../../../../../common/components/Form/Layout/SimpleFormRow";
import { isUSA } from "../../../../../common/utils";
import { SharedAddressInput } from "../../../../SharedAddressInput";
import { useBankValidation } from "../../../hooks/use-bank-validation";
import { getCountryValue, getNameFromCountry } from "../../../mappers";
import { BankAddressInputFields } from "../../BankValidators/constants";
import { RoutingNumberInput } from "../../BankValidators/RoutingNumberInput";
import { SwiftCodeInput } from "../../BankValidators/SwiftCodeInput";
import { ExpandableSection } from "../../ExpandableSection";
import { BankDetailsInputFields, type BankDetailsInputType } from "./constants";
import {
  expandableSectionCollapsed,
  expandableSectionExpanded,
  halfSizeInput,
  intermediaryBank,
} from "./styles_vanilla.css";
import { useResetBankDetailsOnFormChange } from "./utils";

export const BankDetails: React.FC = () => {
  const { values } = useFormContext<BankDetailsInputType>();
  const [showBankInfo, setShowBankInfo] = useState<boolean>(false);
  const [showIntermediaryBankInfo, setShowIntermediaryBankInfo] = useState<boolean>(false);

  useResetBankDetailsOnFormChange();

  // Initialize handlers for primary bank
  const {
    handleClear: handleBankCountryValidatedCountryClear,
    handleValidateCompleted: handleValidateRoutingCompleted,
  } = useBankValidation({
    validatedCountryField: BankDetailsInputFields.BankCountryValidatedCountry,
    nameField: BankDetailsInputFields.BankName,
    addressField: BankDetailsInputFields.BankAddress,
  });

  // Initialize handlers for intermediary bank
  const {
    handleClear: handleIntermediaryBankCountryValidatedCountryClear,
    handleValidateCompleted: handleValidateIntermediaryRoutingCompleted,
  } = useBankValidation({
    validatedCountryField: BankDetailsInputFields.IntermediaryBankCountryValidatedCountry,
    nameField: BankDetailsInputFields.IntermediaryBankName,
    addressField: BankDetailsInputFields.IntermediaryBankAddress,
  });

  return (
    <FieldGroup title="Bank details">
      <SimpleFormRow>
        <CountrySelectionDropDown
          label="Bank country"
          name={BankDetailsInputFields.BankCountry}
          getValue={getCountryValue}
          className={halfSizeInput}
        />
      </SimpleFormRow>
      {values.bankCountry && (
        <>
          {isUSA(getNameFromCountry(values.bankCountry)) ? (
            <RoutingNumberInput
              useAbaPrefix
              onClear={handleBankCountryValidatedCountryClear}
              onValidationCompleted={handleValidateRoutingCompleted}
            />
          ) : (
            <>
              <SwiftCodeInput
                onClear={handleBankCountryValidatedCountryClear}
                onValidationCompleted={handleValidateRoutingCompleted}
              />
              <SimpleFormRow>
                <FormTextInput
                  label="International routing number"
                  name={BankDetailsInputFields.InternationalRoutingNumber}
                  helpText="Optional"
                  className={halfSizeInput}
                />
              </SimpleFormRow>
            </>
          )}

          <ExpandableSection<BankDetailsInputType>
            buttonLabel="Bank address"
            setIsExpanded={setShowBankInfo}
            isExpanded={showBankInfo}
            className={!showBankInfo ? expandableSectionCollapsed : expandableSectionExpanded}
            errorFocusFieldNames={[
              `${BankDetailsInputFields.BankAddress}.${BankAddressInputFields.Region}`,
              `${BankDetailsInputFields.BankAddress}.${BankAddressInputFields.Locality}`,
              `${BankDetailsInputFields.BankAddress}.${BankAddressInputFields.Line1}`,
              `${BankDetailsInputFields.BankAddress}.${BankAddressInputFields.Line2}`,
              `${BankDetailsInputFields.BankAddress}.${BankAddressInputFields.PostalCode}`,
            ]}
          >
            <SharedAddressInput
              title="Address"
              CountrySelect={null}
              testIsUSA={(country) => isUSA(getNameFromCountry(country))}
              name={BankDetailsInputFields.BankAddress}
              fieldNames={{
                state: `${BankDetailsInputFields.BankAddress}.${BankAddressInputFields.Region}`,
                province: `${BankDetailsInputFields.BankAddress}.${BankAddressInputFields.Region}`,
                country: BankDetailsInputFields.BankCountry,
                city: `${BankDetailsInputFields.BankAddress}.${BankAddressInputFields.Locality}`,
                streetLine1: `${BankDetailsInputFields.BankAddress}.${BankAddressInputFields.Line1}`,
                streetLine2: `${BankDetailsInputFields.BankAddress}.${BankAddressInputFields.Line2}`,
                zip: `${BankDetailsInputFields.BankAddress}.${BankAddressInputFields.PostalCode}`,
              }}
            />
          </ExpandableSection>

          <SimpleFormRow>
            <FormCheckbox
              name={BankDetailsInputFields.UsesIntermediaryBank}
              label="This bank uses an intermediary bank for wire transfers"
            />
          </SimpleFormRow>
        </>
      )}
      {values.usesIntermediaryBank && (
        <div className={intermediaryBank}>
          <SimpleFormRow>
            <CountrySelectionDropDown
              label="Bank country"
              name={BankDetailsInputFields.IntermediaryBankCountry}
              getValue={getCountryValue}
              className={halfSizeInput}
            />
          </SimpleFormRow>
          {values.intermediaryBankCountry && (
            <>
              {isUSA(getNameFromCountry(values.intermediaryBankCountry)) ? (
                <RoutingNumberInput
                  useAbaPrefix
                  onClear={handleIntermediaryBankCountryValidatedCountryClear}
                  onValidationCompleted={handleValidateIntermediaryRoutingCompleted}
                  formFieldKeys={{
                    routingNumber: BankDetailsInputFields.IntermediaryRoutingNumber,
                    bankName: BankDetailsInputFields.IntermediaryBankName,
                  }}
                />
              ) : (
                <SwiftCodeInput
                  onValidationCompleted={handleValidateIntermediaryRoutingCompleted}
                  onClear={handleIntermediaryBankCountryValidatedCountryClear}
                  formFieldKeys={{
                    swiftCode: BankDetailsInputFields.IntermediarySwiftCode,
                    bankName: BankDetailsInputFields.IntermediaryBankName,
                  }}
                />
              )}
              {!isUSA(getNameFromCountry(values.intermediaryBankCountry)) && (
                <SimpleFormRow>
                  <FormTextInput
                    label="International routing number"
                    name={BankDetailsInputFields.IntermediaryInternationalRoutingNumber}
                    helpText="Optional"
                    className={halfSizeInput}
                  />
                </SimpleFormRow>
              )}
              <ExpandableSection<BankDetailsInputType>
                buttonLabel="Bank address"
                setIsExpanded={setShowIntermediaryBankInfo}
                isExpanded={showIntermediaryBankInfo}
                className={
                  !showIntermediaryBankInfo
                    ? expandableSectionCollapsed
                    : expandableSectionExpanded
                }
                errorFocusFieldNames={[
                  `${BankDetailsInputFields.IntermediaryBankAddress}.${BankAddressInputFields.Region}`,
                  `${BankDetailsInputFields.IntermediaryBankAddress}.${BankAddressInputFields.Locality}`,
                  `${BankDetailsInputFields.IntermediaryBankAddress}.${BankAddressInputFields.Line1}`,
                  `${BankDetailsInputFields.IntermediaryBankAddress}.${BankAddressInputFields.Line2}`,
                  `${BankDetailsInputFields.IntermediaryBankAddress}.${BankAddressInputFields.PostalCode}`,
                ]}
              >
                <SharedAddressInput
                  title="Address"
                  CountrySelect={null}
                  testIsUSA={(country) => isUSA(getNameFromCountry(country))}
                  name={BankDetailsInputFields.IntermediaryBankAddress}
                  fieldNames={{
                    state: `${BankDetailsInputFields.IntermediaryBankAddress}.${BankAddressInputFields.Region}`,
                    province: `${BankDetailsInputFields.IntermediaryBankAddress}.${BankAddressInputFields.Region}`,
                    country: BankDetailsInputFields.IntermediaryBankCountry,
                    city: `${BankDetailsInputFields.IntermediaryBankAddress}.${BankAddressInputFields.Locality}`,
                    streetLine1: `${BankDetailsInputFields.IntermediaryBankAddress}.${BankAddressInputFields.Line1}`,
                    streetLine2: `${BankDetailsInputFields.IntermediaryBankAddress}.${BankAddressInputFields.Line2}`,
                    zip: `${BankDetailsInputFields.IntermediaryBankAddress}.${BankAddressInputFields.PostalCode}`,
                  }}
                />
              </ExpandableSection>
            </>
          )}
        </div>
      )}
    </FieldGroup>
  );
};
